import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTOSAVE_STATUS } from 'utils/autosave';

const identity = (value) => value;
const defaultSavedAt = (result) => result?.modified_on || Date.now();

/**
 * Shared autosave lifecycle. Consumers own their presentation state and provide
 * the current value; this hook owns the persisted baseline, scheduling, queue,
 * revision safety, and status lifecycle.
 */
const useAutosaveController = ({
    value,
    enabled = true,
    autoSaveEnabled = enabled,
    debounceMs = 800,
    persist,
    normalize = identity,
    validate = () => true,
    onInvalid,
    onSuccess,
    onError,
    getSavedAt = defaultSavedAt,
    equality = isEqual,
    initialLastSavedAt,
}) => {
    const valueRef = useRef(value);
    const observedRef = useRef();
    const baselineRef = useRef();
    const timerRef = useRef(null);
    const revisionRef = useRef(0);
    const savingRef = useRef(false);
    const activePromiseRef = useRef(null);
    const idlePromiseRef = useRef(null);
    const resolveIdleRef = useRef(null);
    const queuedRef = useRef(null);
    const mountedRef = useRef(true);
    const pausedRef = useRef(false);
    const [isDirty, setIsDirty] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(initialLastSavedAt || null);
    const [status, setStatus] = useState(AUTOSAVE_STATUS.IDLE);
    const [error, setError] = useState(null);

    valueRef.current = value;

    const clearTimer = useCallback(() => {
        if (timerRef.current === null) return;
        clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    const canonicalize = useCallback((next) => normalize(next), [normalize]);

    const currentSnapshot = useCallback(() => {
        const snapshot = canonicalize(valueRef.current);
        return { snapshot, revision: revisionRef.current };
    }, [canonicalize]);

    const isCurrentBaseline = useCallback(
        (snapshot) => baselineRef.current !== undefined && equality(snapshot, baselineRef.current),
        [equality]
    );

    const persistSnapshot = useCallback(
        async (snapshot, revision, reason) => {
            if (!mountedRef.current) return false;
            if (!validate(snapshot, { reason })) {
                if (reason !== 'debounce') onInvalid?.(snapshot, { reason });
                if (mountedRef.current) {
                    setIsDirty(true);
                    setStatus(AUTOSAVE_STATUS.PENDING);
                }
                return false;
            }

            if (isCurrentBaseline(snapshot)) {
                if (mountedRef.current) setStatus(AUTOSAVE_STATUS.SAVED);
                return true;
            }

            savingRef.current = true;
            if (!idlePromiseRef.current) {
                idlePromiseRef.current = new Promise((resolve) => {
                    resolveIdleRef.current = resolve;
                });
            }
            setStatus(AUTOSAVE_STATUS.SAVING);
            setError(null);
            let outcome = false;
            try {
                activePromiseRef.current = persist(snapshot);
                const result = await activePromiseRef.current;
                if (!mountedRef.current) return false;

                const isCurrent = revision === revisionRef.current;
                if (!isCurrent) {
                    queuedRef.current = queuedRef.current || currentSnapshot();
                    outcome = true;
                } else {
                    baselineRef.current = snapshot;
                    setLastSavedAt(getSavedAt(result));
                    setIsDirty(false);
                    setStatus(AUTOSAVE_STATUS.SAVED);
                    onSuccess?.(result, snapshot);
                    outcome = true;
                }
            } catch (requestError) {
                if (mountedRef.current && revision === revisionRef.current) {
                    setIsDirty(true);
                    setStatus(AUTOSAVE_STATUS.ERROR);
                    setError(requestError);
                    onError?.(requestError, snapshot);
                }
            } finally {
                savingRef.current = false;
                activePromiseRef.current = null;
                if (mountedRef.current && !pausedRef.current) {
                    const latest = currentSnapshot();
                    const hasNewerSnapshot = latest.revision !== revision && !isCurrentBaseline(latest.snapshot);
                    const queuedSnapshot = queuedRef.current;
                    const shouldPersistNewerSnapshot =
                        queuedSnapshot?.revision > revision || (outcome && hasNewerSnapshot);
                    if (shouldPersistNewerSnapshot) {
                        queuedRef.current = null;
                        const nextSnapshot = queuedSnapshot || latest;
                        outcome = await persistSnapshot(nextSnapshot.snapshot, nextSnapshot.revision, 'debounce');
                    } else {
                        queuedRef.current = null;
                    }
                }
                resolveIdleRef.current?.(outcome);
                resolveIdleRef.current = null;
                idlePromiseRef.current = null;
            }
            return outcome;
        },
        [currentSnapshot, getSavedAt, isCurrentBaseline, onError, onInvalid, onSuccess, persist, validate]
    );

    const flush = useCallback(
        async ({ reason = 'manual', nextValue } = {}) => {
            clearTimer();
            if (nextValue !== undefined) {
                valueRef.current = nextValue;
                observedRef.current = canonicalize(nextValue);
                revisionRef.current += 1;
            }
            if (!enabled || pausedRef.current || valueRef.current == null) return false;
            const { snapshot, revision } = currentSnapshot();
            if (savingRef.current) {
                queuedRef.current = { snapshot, revision };
                return idlePromiseRef.current || activePromiseRef.current || false;
            }
            return persistSnapshot(snapshot, revision, reason);
        },
        [canonicalize, clearTimer, currentSnapshot, enabled, persistSnapshot]
    );

    const schedule = useCallback(() => {
        clearTimer();
        if (!autoSaveEnabled || pausedRef.current || valueRef.current == null) return;
        const { snapshot } = currentSnapshot();
        if (isCurrentBaseline(snapshot)) return;
        setIsDirty(true);
        setStatus(AUTOSAVE_STATUS.PENDING);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            flush({ reason: 'debounce' });
        }, debounceMs);
    }, [autoSaveEnabled, clearTimer, currentSnapshot, debounceMs, flush, isCurrentBaseline]);

    const reset = useCallback(
        (next, { lastSavedAt: timestamp } = {}) => {
            clearTimer();
            revisionRef.current += 1;
            valueRef.current = next;
            baselineRef.current = canonicalize(next);
            observedRef.current = baselineRef.current;
            queuedRef.current = null;
            setIsDirty(false);
            setError(null);
            setLastSavedAt(timestamp || null);
            setStatus(timestamp ? AUTOSAVE_STATUS.SAVED : AUTOSAVE_STATUS.IDLE);
        },
        [canonicalize, clearTimer]
    );

    const markDirty = useCallback(() => {
        baselineRef.current = undefined;
        revisionRef.current += 1;
        setIsDirty(true);
        setStatus(AUTOSAVE_STATUS.PENDING);
        schedule();
    }, [schedule]);

    const pause = useCallback(() => {
        pausedRef.current = true;
        clearTimer();
    }, [clearTimer]);

    const resume = useCallback(() => {
        pausedRef.current = false;
        if (isDirty) schedule();
    }, [isDirty, schedule]);

    const save = useCallback(() => flush({ reason: 'manual' }), [flush]);
    const waitForIdle = useCallback(() => idlePromiseRef.current || activePromiseRef.current || Promise.resolve(), []);

    useEffect(() => {
        if (!enabled || valueRef.current == null) return;
        if (baselineRef.current === undefined) {
            baselineRef.current = canonicalize(valueRef.current);
            observedRef.current = baselineRef.current;
            setLastSavedAt(initialLastSavedAt || null);
            setStatus(initialLastSavedAt ? AUTOSAVE_STATUS.SAVED : AUTOSAVE_STATUS.IDLE);
            return;
        }
        const snapshot = canonicalize(valueRef.current);
        if (!equality(snapshot, observedRef.current)) {
            observedRef.current = snapshot;
            revisionRef.current += 1;
            schedule();
        }
    }, [canonicalize, enabled, equality, initialLastSavedAt, schedule, value]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimer();
        };
    }, [clearTimer]);

    return {
        cancelScheduledSave: clearTimer,
        error,
        flush,
        isDirty,
        lastSavedAt,
        markDirty,
        pause,
        reset,
        resume,
        save,
        status,
        waitForIdle,
    };
};

export default useAutosaveController;
